import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  BookOpen, CalendarDays, CreditCard, Clock, Building2,
  ArrowRight, AlertTriangle, X, MapPin, CheckCircle2, KeyRound, Users, UserPlus, Mail
} from "lucide-react";

function Section({ icon: Icon, title, children, id }) {
  return (
    <section id={id} className="scroll-mt-24">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-9 w-9 rounded-lg bg-green-500/10 flex items-center justify-center">
          <Icon className="h-5 w-5 text-green-600" />
        </div>
        <h2 className="text-xl font-heading font-bold text-rig-charcoal">{title}</h2>
      </div>
      <div className="text-sm text-rig-charcoal/80 leading-relaxed space-y-3 pl-12">
        {children}
      </div>
    </section>
  );
}

function Step({ n, children }) {
  return (
    <div className="flex gap-3 items-start">
      <span className="flex-shrink-0 h-6 w-6 rounded-full bg-green-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">{n}</span>
      <span>{children}</span>
    </div>
  );
}

const TOC = [
  { id: "overview", label: "Getting Started", icon: BookOpen },
  { id: "signin", label: "Sign-In Options", icon: KeyRound },
  { id: "booking", label: "Booking a Space", icon: CalendarDays },
  { id: "guests", label: "Guest Registration", icon: UserPlus },
  { id: "plan", label: "Your Plan & Hours", icon: CreditCard },
  { id: "manage", label: "Managing Bookings", icon: Clock },
  { id: "inbox", label: "Admin Messages", icon: Mail },
  { id: "cancellation", label: "Cancellation Policy", icon: AlertTriangle },
  { id: "calendar", label: "Calendar Integration", icon: CalendarDays },
];

export default function MemberGuide() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Member Guide | The Rig";
    if (!authLoading && (!user || user.role !== "member")) navigate("/login");
  }, [user, authLoading, navigate]);

  if (authLoading) return null;

  return (
    <div className="max-w-5xl mx-auto px-6 py-12" data-testid="member-guide">
      <title>Member Guide | The Rig</title>

      <div className="mb-10">
        <Badge className="bg-green-100 text-green-800 border-0 mb-3">Member Documentation</Badge>
        <h1 className="text-3xl sm:text-4xl font-heading font-extrabold text-rig-charcoal mb-2">
          Member Guide
        </h1>
        <p className="text-rig-charcoal/60 text-base max-w-2xl">
          Your guide to booking meeting rooms, tracking your plan hours, and getting the most out of your Rig membership.
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-10">
        <nav className="lg:w-52 flex-shrink-0">
          <Card className="p-4 lg:sticky lg:top-24">
            <p className="text-xs font-bold uppercase tracking-wider text-rig-charcoal/40 mb-3">Contents</p>
            <ul className="space-y-1">
              {TOC.map(t => (
                <li key={t.id}>
                  <a href={`#${t.id}`} className="flex items-center gap-2 text-sm text-rig-charcoal/70 hover:text-green-600 py-1 transition-colors">
                    <t.icon className="h-3.5 w-3.5" />
                    {t.label}
                  </a>
                </li>
              ))}
            </ul>
          </Card>
        </nav>

        <div className="flex-1 space-y-10">
          <Section icon={BookOpen} title="Getting Started" id="overview">
            <p>
              Welcome to <strong>The Rig</strong>! As a member, you can book meeting rooms using your plan's included hours. Your bookings are confirmed instantly when covered by your plan.
            </p>
            <p>Your dashboard is at <strong>"My Bookings"</strong> in the navigation — it shows all your upcoming and past bookings, plus your plan details.</p>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {[
                { label: "View bookings", icon: CalendarDays },
                { label: "Track hours", icon: Clock },
                { label: "Book rooms", icon: Building2 },
              ].map(f => (
                <div key={f.label} className="bg-rig-charcoal/5 rounded-lg p-3 text-center">
                  <f.icon className="h-5 w-5 mx-auto text-green-600 mb-1" />
                  <p className="text-xs font-medium text-rig-charcoal">{f.label}</p>
                </div>
              ))}
            </div>
          </Section>

          <Separator />

          <Section icon={KeyRound} title="Sign-In Options" id="signin">
            <p>You can sign in to The Rig in two ways:</p>
            <ul className="list-disc ml-4 space-y-1">
              <li><strong>Email & password</strong> — use the credentials sent to your email when your account was created.</li>
              <li><strong>One-time passcode</strong> — click <strong>"Sign in with passcode"</strong> on the login page, enter your email, and receive a 6-digit code. It expires in 5 minutes.</li>
            </ul>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-2">
              <p className="font-semibold text-green-800 text-xs flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Tip</p>
              <p className="text-green-700 mt-1">The passcode option is great if you've forgotten your password — no need to contact your manager or admin.</p>
            </div>
          </Section>

          <Separator />

          <Section icon={CalendarDays} title="Booking a Space" id="booking">
            <p><strong>How to book a meeting room:</strong></p>
            <Step n={1}>Click <strong>"Spaces"</strong> in the navigation or <strong>"Book Now"</strong> button.</Step>
            <Step n={2}>Browse available rooms. Each shows capacity, available layouts, and pricing.</Step>
            <Step n={3}>Click a room to see full details, photos, and today's availability calendar.</Step>
            <Step n={4}>Click <strong>"Book Now"</strong> and select your booking type (hourly, daily, or monthly).</Step>
            <Step n={5}>Pick your date and time slots from the calendar. Occupied slots are shown in red.</Step>
            <Step n={6}>Choose your preferred room layout (boardroom, classroom, theater, U-shape, or banquet).</Step>
            <Step n={7}>Add a <strong>Meeting Name</strong> (optional) — this appears on room display screens and in your confirmation email.</Step>
            <Step n={8}>If you have team members, you can select <strong>"Booking For"</strong> to book on behalf of a colleague. The dropdown shows all members of your team.</Step>
            <Step n={9}>Review your booking details and confirm. If plan hours are available, your booking is confirmed instantly!</Step>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
              <p className="font-semibold text-blue-800 text-xs flex items-center gap-1"><Users className="h-3.5 w-3.5" /> Booking for a Teammate</p>
              <p className="text-blue-700 mt-1">When you book for a colleague, the hours are deducted from your team's shared plan. Both you and the person booked for will receive confirmation emails.</p>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-2">
              <p className="font-semibold text-green-800 text-xs flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Plan-Covered Bookings</p>
              <p className="text-green-700 mt-1">When your plan has sufficient hours, bookings are confirmed immediately — no payment needed. The hours are deducted from your monthly allowance.</p>
            </div>
          </Section>

          <Separator />

          <Section icon={UserPlus} title="Guest Registration" id="guests">
            <p>You can pre-register guests at any time — no booking required.</p>

            <p className="mt-2"><strong>From your dashboard:</strong></p>
            <Step n={1}>Click <strong>"Register Guest"</strong> at the top of your bookings page.</Step>
            <Step n={2}>Enter the guest's name, email (optional), and purpose of visit.</Step>
            <Step n={3}>If an email is provided, your guest receives a <strong>QR code</strong> they can scan at the lobby kiosk to check in instantly. You'll be notified when they arrive.</Step>

            <p className="mt-2"><strong>During a booking:</strong></p>
            <p>In step 3 of any booking, find <strong>"Pre-register Guests"</strong> to add visitors for a specific meeting. Each guest gets a personalized email with meeting details and a QR check-in code.</p>

            <p className="mt-2"><strong>How your guest checks in:</strong></p>
            <p>When they arrive at The Rig, pre-registered guests can use the lobby kiosk to:</p>
            <ul className="list-disc ml-4 space-y-1">
              <li><strong>Scan their QR code</strong> from the email at the kiosk camera</li>
              <li><strong>Enter their check-in code</strong> manually</li>
              <li><strong>Click the link</strong> in their email from their phone</li>
            </ul>
            <p className="mt-1 text-xs text-muted-foreground">You'll receive an email notification the moment they check in.</p>

            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-2">
              <p className="font-semibold text-green-800 text-xs flex items-center gap-1"><UserPlus className="h-3.5 w-3.5" /> Saved/Frequent Guests</p>
              <p className="text-green-700 mt-1">Every guest you register is <strong>automatically saved</strong> to your contact book. Use <strong>"Quick add from frequent guests"</strong> in the booking flow to add repeat visitors in one click — no retyping needed.</p>
            </div>
          </Section>

          <Separator />

          <Section icon={CreditCard} title="Your Plan & Hours" id="plan">
            <p>Your membership plan includes:</p>
            <ul className="list-disc ml-4 space-y-1">
              <li><strong>Monthly included hours</strong> — use these to book rooms at no extra cost</li>
              <li><strong>Room access</strong> — specific rooms your plan covers</li>
              <li><strong>Price per hour</strong> — rate for hours beyond your plan (in XCG)</li>
            </ul>

            <p className="mt-2">Check your remaining hours on your <strong>My Bookings</strong> dashboard. The profile card shows:</p>
            <ul className="list-disc ml-4 space-y-1">
              <li>Your plan name</li>
              <li>Hours used vs. included hours</li>
              <li>Your company name</li>
            </ul>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
              <p className="font-semibold text-amber-800 text-xs flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Running Low?</p>
              <p className="text-amber-700 mt-1">If your plan hours are exhausted, you can still book — but the booking will require payment. Contact your manager or The Rig admin to discuss upgrading your plan.</p>
            </div>
          </Section>

          <Separator />

          <Section icon={Clock} title="Managing Bookings" id="manage">
            <p>From your <strong>My Bookings</strong> dashboard you can:</p>
            <ul className="list-disc ml-4 space-y-1">
              <li>View all your <strong>upcoming</strong> and <strong>past</strong> bookings in separate tabs</li>
              <li>See booking details: date, time, room, layout, and status</li>
              <li><strong>Edit</strong> a booking using the pencil icon — change the meeting name, date, time, or notes</li>
              <li><strong>Cancel</strong> a booking using the X button</li>
            </ul>
            <p className="mt-2">When you edit a booking, an updated confirmation email with a new calendar invite is sent to you automatically.</p>

            <p className="mt-2"><strong>Booking statuses:</strong></p>
            <div className="flex flex-wrap gap-2 mt-1">
              {[
                { status: "confirmed", color: "bg-green-100 text-green-800", desc: "Booking is active" },
                { status: "pending_payment", color: "bg-amber-100 text-amber-800", desc: "Awaiting payment" },
                { status: "pending_cancellation", color: "bg-orange-100 text-orange-800", desc: "Cancel request sent" },
                { status: "cancelled", color: "bg-gray-100 text-gray-800", desc: "Booking was cancelled" },
              ].map(s => (
                <div key={s.status} className="flex items-center gap-1.5">
                  <Badge className={`${s.color} border-0 text-xs`}>{s.status}</Badge>
                  <span className="text-xs text-rig-charcoal/50">— {s.desc}</span>
                </div>
              ))}
            </div>
          </Section>

          <Separator />

          <Section icon={Mail} title="Admin Messages" id="inbox">
            <p>The Rig admin may send you important announcements or updates. These appear directly on your <strong>My Bookings dashboard</strong> with an orange highlight.</p>
            <ul className="list-disc ml-4 space-y-1">
              <li>Unread messages appear prominently at the top of your dashboard</li>
              <li>You'll also receive the message via <strong>email</strong></li>
              <li>Click <strong>"Dismiss"</strong> to mark a message as read</li>
            </ul>
          </Section>

          <Separator />

          <Section icon={AlertTriangle} title="Cancellation Policy" id="cancellation">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="font-semibold text-red-800 text-sm">12-Hour Rule</p>
              <ul className="list-disc ml-4 space-y-1 text-red-700 mt-1">
                <li>Cancel <strong>more than 12 hours</strong> before start → instant cancellation.</li>
                <li>Cancel <strong>less than 12 hours</strong> before start for a <strong>paid booking</strong> → goes to admin for approval.</li>
                <li>Unpaid bookings can always be cancelled immediately.</li>
              </ul>
            </div>
            <p className="mt-2">If your cancellation requires admin approval, the status changes to <Badge className="bg-orange-100 text-orange-800 border-0 text-xs">pending_cancellation</Badge> — you'll be notified once it's resolved.</p>
          </Section>

          <Separator />

          <Section icon={CalendarDays} title="Calendar Integration" id="calendar">
            <p>After a booking is confirmed, you get options to sync with your calendar:</p>
            <ul className="list-disc ml-4 space-y-1">
              <li><strong>Google Calendar</strong> — one-click add to your Google Calendar</li>
              <li><strong>Outlook</strong> — direct link to add to Outlook calendar</li>
              <li><strong>.ics download</strong> — universal calendar file for any calendar app</li>
            </ul>
            <p className="mt-2">You'll also receive a <strong>confirmation email</strong> with an .ics calendar invite attached automatically.</p>
          </Section>

          <Separator />

          <div className="text-center py-8 text-rig-charcoal/40 text-sm">
            <p>Need help? Contact your manager or email <a href="mailto:info@ic-foundation.org" className="text-green-600 underline">info@ic-foundation.org</a></p>
          </div>
        </div>
      </div>
    </div>
  );
}
